import { z } from 'zod'

import {
  nullableString,
  nullableNumber,
  isoDateSchema,
  serverItemSchema,
} from '../utils/schema'
import { profileBaseSchema } from '../schema/profile.schema'

type AdvancedClientDetail = z.infer<typeof advancedClientDetailSchema>
type BaseClientDetail = z.infer<typeof baseClientDetailSchema>
type PosName = z.infer<typeof phoneNumberSchema>['posName']

const e164Schema = z.string()

const optInStateSchema = z.enum([
  'NOT_INITIALIZED',
  'PENDING',
  'OPTED_IN',
  'OPTED_OUT',
])

const posNameOrderSchema = z
  .string()
  .pipe(
    z
      .enum([
        'Sign Up',
        'Cell',
        'Mobile',
        'Primary Phone',
        'Mobile 1',
        'Mobile 2',
        'Phone Number',
        'Phone',
        'Phone 1',
        'phone1',
        'Phone 2',
        'Phone 3',
        'Phone 4',
        'Address Phone',
        'Work Phone',
        'Home Phone',
        'Unknown',
        'Unknown Type',
      ])
      .catch('Unknown Type')
  )

const phoneNumberSchema = serverItemSchema
  .extend({
    default: z.boolean(),
    e164: e164Schema.nullable(),
    optInState: optInStateSchema,
    phoneNumber: z.string(),
    posName: posNameOrderSchema.or(z.undefined()).nullable(),
    type: nullableString,
    verified: z.boolean(),
  })
  .transform((obj) => ({ ...obj, posName: obj.posName || undefined }))

const tagSchema = serverItemSchema.merge(z.object({ name: z.string() }))

const baseClientDetailSchema = profileBaseSchema.extend({
  allowSms: z.boolean(),
  birthday: nullableNumber,
  dobObject: z
    .object({
      day: z.number().optional().nullable(), // some errant bad 'null' data on server
      month: z.number().optional().nullable(), // some errant bad 'null' data on server
      year: z.number().optional().nullable(), // some errant bad 'null' data on server
    })
    .nullable(),
  email: nullableString,
  isMine: z.boolean(),
  phoneNumbers: z.array(phoneNumberSchema),
  posLanguage: z.string(),
  unsubscribedAt: isoDateSchema.nullable(),
})

const advancedClientDetailSchema = baseClientDetailSchema.merge(
  z.object({
    updatedAt: isoDateSchema,
    createdAt: isoDateSchema,
    users: z.array(serverItemSchema).optional(),
    nextContact: isoDateSchema.optional(),
    lastContact: isoDateSchema.optional(),
    firstSale: isoDateSchema.optional(),
    lastSale: isoDateSchema.optional(),
    salesTotal: z.number().optional(),
    tags: z.array(tagSchema),
    posActive: z.boolean().optional(),
    allowTwilioSms: z.boolean(),
    anniversaryObject: z
      .object({
        day: z.number().optional(),
        month: z.number().optional(),
        year: z.number().optional(),
      })
      .nullable(),

    street1: nullableString,
    street2: nullableString,
    city: nullableString,
    state: nullableString,
    zip: nullableString,
    country: nullableString,

    recentSales: z
      .array(
        z.object({
          posPhotoUrls: z.array(z.string()),
          finalValue: nullableNumber,
        })
      )
      .optional(),
  })
)

export type { AdvancedClientDetail, BaseClientDetail, PosName }
export { advancedClientDetailSchema, baseClientDetailSchema, phoneNumberSchema }
